import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2a2f3baa"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "organization-limits-section",
  class: "d-flex flex-column"
}
const _hoisted_2 = { class: "margin-top-10 organization-limits-box" }
const _hoisted_3 = { class: "d-flex flex-row align-items-center organization-limits-box-header" }
const _hoisted_4 = { class: "organization-limits-fields" }
const _hoisted_5 = { class: "d-flex flex-row align-items-center" }

import { computed, onMounted, ref } from "vue";
import { ElInput, ElInputNumber, ElTooltip } from "element-plus";
import { TYPE } from "vue-toastification";
import {
  getOrganizationConfig,
  getTenantLevelConfig,
  updateOrganizationConfig,
} from "@/organization/services/organization.service";
import { hasPermission } from "@/core/auth";
import { showAlert } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import {
  EDIT_LIMITS_PRIVILEGE,
  ORGANIZATION_LIMITS,
  TENANT_CEILING_KEY,
  OrganizationLimit,
  buildOrganizationSavePayload,
  isLimitValueValid,
  isTenantCeilingEnforced,
  isWithinTenantLimit,
  toDisplayValue,
} from "@/organization/utils/organizationSettings";

interface LimitState {
  limit: OrganizationLimit;
  // False until this limit's read succeeds; an unloaded field shows "unavailable", never a fallback.
  loaded: boolean;
  // The DTO as loaded (null when none exists); spread into the save payload only when it is the
  // organization's own row.
  config: any | null;
  // The value the field started from; a limit is saved only when value differs from it.
  initialValue: number | null;
  value: number | null;
  // The tenant's own value: the ceiling for this organization's value. Read only for an editor;
  // null until that read succeeds, and a limit without it is not editable.
  tenantValue: number | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OrganizationLimitsSection',
  props: {
    organizationId: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const { t } = i18n.global;

const states = ref<LimitState[]>(
  ORGANIZATION_LIMITS.map((limit) => ({
    limit,
    loaded: false,
    config: null,
    initialValue: null,
    value: null,
    tenantValue: null,
  }))
);

// A UI check only: the server does not check this privilege (design §10-D5, §15.1).
const canEdit = computed(() => hasPermission(EDIT_LIMITS_PRIVILEGE));

// The tenant's datastore.limits.tenant-ceiling switch (design §16), on unless the tenant turned it off.
// While it is off the backend lets an organization exceed its tenant, so the page neither reads the
// tenant values nor checks against them. A failed read of the switch keeps the ceiling, the stricter
// choice.
const ceilingEnforced = ref(false);

function fieldId(limit: OrganizationLimit): string {
  return `organization-limit-${limit.key.replace(/\./g, "-")}`;
}

async function load(): Promise<void> {
  if (!props.organizationId) {
    return;
  }
  // Each limit is read on its own, so one failed read marks only that field unavailable.
  const results = await Promise.allSettled(
    states.value.map((state) =>
      getOrganizationConfig(state.limit.key, props.organizationId)
    )
  );
  // An editor also needs the ceiling switch and, while it is on, each tenant value; a failed tenant
  // value leaves that limit read-only.
  let switchFailed = false;
  if (canEdit.value) {
    try {
      ceilingEnforced.value = isTenantCeilingEnforced(
        await getTenantLevelConfig(TENANT_CEILING_KEY)
      );
    } catch (error) {
      console.error(`Error loading '${TENANT_CEILING_KEY}':`, error);
      ceilingEnforced.value = true;
      switchFailed = true;
    }
  }
  // The tenant values are read whether or not the ceiling is on: on, they are the limit; off, they
  // are shown as the tenant default.
  const tenantResults = canEdit.value
    ? await Promise.allSettled(
        states.value.map((state) => getTenantLevelConfig(state.limit.key))
      )
    : [];
  tenantResults.forEach((result, index) => {
    const state = states.value[index];
    if (result.status === "fulfilled") {
      state.tenantValue = toDisplayValue(state.limit, result.value);
    } else {
      console.error(
        `Error loading tenant limit '${state.limit.key}':`,
        result.reason
      );
    }
  });
  results.forEach((result, index) => {
    const state = states.value[index];
    if (result.status === "fulfilled") {
      state.loaded = true;
      state.config = result.value;
      state.initialValue = toDisplayValue(state.limit, result.value);
      state.value = state.initialValue;
    } else {
      console.error(
        `Error loading organization limit '${state.limit.key}':`,
        result.reason
      );
    }
  });
  if (
    switchFailed ||
    [...results, ...tenantResults].some(
      (result) => result.status === "rejected"
    )
  ) {
    showAlert(TYPE.ERROR, t("OrganizationLimits.LoadFailed"));
  }
}

function isEditable(state: LimitState): boolean {
  return (
    canEdit.value &&
    state.loaded &&
    (!ceilingEnforced.value || state.tenantValue !== null)
  );
}

function isChanged(state: LimitState): boolean {
  return isEditable(state) && state.value !== state.initialValue;
}

// Shown for any value above the tenant's, including one saved before this check existed; only a
// changed value blocks the save, so such a value does not stop the other limits being saved.
function exceedsTenant(state: LimitState): boolean {
  return (
    ceilingEnforced.value &&
    typeof state.value === "number" &&
    typeof state.tenantValue === "number" &&
    !isWithinTenantLimit(state.value, state.tenantValue)
  );
}

/**
 * Checks every changed limit; warns about the first invalid one, or the first one above the
 * tenant's value.
 */
function validate(): boolean {
  const changed = states.value.filter(isChanged);
  const invalid = changed.find(
    (state) => !isLimitValueValid(state.limit, state.value)
  );
  if (invalid) {
    showAlert(
      TYPE.WARNING,
      t("OrganizationLimits.InvalidValue", { label: t(invalid.limit.labelKey) })
    );
    return false;
  }
  const aboveTenant = changed.find(exceedsTenant);
  if (aboveTenant) {
    showAlert(
      TYPE.WARNING,
      t("OrganizationLimits.AboveTenantLimit", {
        label: t(aboveTenant.limit.labelKey),
        value: aboveTenant.tenantValue,
      })
    );
    return false;
  }
  return true;
}

/**
 * Writes only the limits that were changed, as ORGANIZATION rows. An untouched limit writes nothing,
 * so the organization keeps inheriting. Resolves true when every write succeeded, and false — never
 * rejects — when any write failed or threw, so the host's failure path handles it.
 *
 * Each successful write becomes the limit's new baseline (initialValue = the value written, config =
 * the persisted row the PUT returns), so if the page stays open after a partly failed save, a saved
 * limit set back to its old value is still seen as a change. The persisted row carries the audit
 * fields the backend assigned (createdAt, createdBy); the backend's merge would null them on the next
 * write if the baseline lacked them. Failed limits keep their old baseline and are retried.
 */
async function save(): Promise<boolean> {
  const changed = states.value.filter(isChanged);
  if (changed.length === 0) {
    return true;
  }
  const payloads = changed.map((state) =>
    buildOrganizationSavePayload(
      state.limit,
      state.config,
      props.organizationId,
      state.value as number
    )
  );
  const results = await Promise.allSettled(
    payloads.map((payload) => updateOrganizationConfig(payload))
  );
  let allSaved = true;
  results.forEach((result, index) => {
    if (result.status === "fulfilled" && result.value?.code === 200) {
      changed[index].initialValue = changed[index].value;
      // PUT /configs returns the saved DTO as a one-element result.
      changed[index].config = result.value.result?.[0] ?? payloads[index];
    } else {
      allSaved = false;
    }
  });
  return allSaved;
}

onMounted(load);

__expose({ validate, save });

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("strong", null, _toDisplayString(_unref(t)("OrganizationLimits.Title")), 1),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("OrganizationLimits.GroupTitle")), 1),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, (state) => {
          return (_openBlock(), _createElementBlock("div", {
            key: state.limit.key,
            class: "margin-top-10 d-flex flex-column"
          }, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("strong", null, _toDisplayString(_unref(t)(state.limit.labelKey)), 1),
              _createVNode(_unref(ElTooltip), {
                effect: "custom",
                content: _unref(t)(state.limit.descriptionKey),
                placement: "right"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "info-circle",
                    class: "organization-limits-help-icon"
                  })
                ]),
                _: 2
              }, 1032, ["content"])
            ]),
            (isEditable(state))
              ? (_openBlock(), _createBlock(_unref(ElInputNumber), {
                  key: 0,
                  id: fieldId(state.limit),
                  modelValue: state.value,
                  "onUpdate:modelValue": ($event: any) => ((state.value) = $event),
                  min: 0,
                  max: state.limit.max,
                  step: 1,
                  precision: 0,
                  size: "small",
                  "controls-position": "right"
                }, null, 8, ["id", "modelValue", "onUpdate:modelValue", "max"]))
              : (_openBlock(), _createBlock(_unref(ElInput), {
                  key: 1,
                  id: fieldId(state.limit),
                  "model-value": state.loaded ? String(state.value) : '',
                  placeholder: 
              state.loaded ? '' : _unref(t)('OrganizationLimits.Unavailable')
            ,
                  size: "small",
                  disabled: ""
                }, null, 8, ["id", "model-value", "placeholder"])),
            (isEditable(state) && state.tenantValue !== null)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  class: _normalizeClass([
              'organization-limits-tenant-hint',
              {
                'organization-limits-tenant-hint-exceeded':
                  exceedsTenant(state),
              },
            ])
                }, _toDisplayString(_unref(t)(
                ceilingEnforced.value
                  ? "OrganizationLimits.TenantLimit"
                  : "OrganizationLimits.TenantDefault",
                { value: state.tenantValue }
              )), 3))
              : _createCommentVNode("", true)
          ]))
        }), 128))
      ])
    ])
  ]))
}
}

})